/**
 * @description Very simple utility function to resolve a feature flag value that could be either a boolean or a string.
 * This is useful in cases where we want to allow for more flexible configuration of feature flags,
 * such as using a string to specify a particular variant of a feature, while still supporting the traditional boolean on/off values.
 *
 * @example
 * // Example usage of resolveStringFeatureFlag
 * const myFeatureFlagValue = posthog.getFeatureFlag('my-feature-flag');
 * const resolvedValue = resolveStringFeatureFlag<'variant1' | 'default-variant'>(myFeatureFlagValue, 'default-variant');
 * console.log(resolvedValue); // This will log the string value of the feature flag if it's a string, or 'default-variant' if it's a boolean or undefined.
 *
 * @param {boolean | string | undefined} flagValue - The value of the feature flag, which can be a boolean, a string, or undefined.
 * @param {T} defaultValue - The default value to return if the flagValue is a boolean or undefined.
 * @returns {T} - The resolved value of the feature flag, which will be either the string value of the flag or the provided default value.
 */
export const resolveStringFeatureFlag = <T>(
  flagValue: boolean | string | undefined,
  defaultValue: T,
): T => {
  if (typeof flagValue === 'string') {
    return flagValue as unknown as T;
  } else if (typeof flagValue === 'boolean') {
    return defaultValue;
  } else return defaultValue;
};
